import React from 'react';
import {
  Box,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';

export type AnalysisField =
  | 'keyFacts'
  | 'contentSeeds'
  | 'quotes'
  | 'insightCandidates'
  | 'contradictions';

export const ALL_ANALYSIS_FIELDS: AnalysisField[] = [
  'keyFacts',
  'contentSeeds',
  'quotes',
  'insightCandidates',
  'contradictions',
];

export interface ResearchRunSelection {
  runId: string;
  includeAnalysisFields?: AnalysisField[];
  includeChunkIds?: number[];
  includeFullResearch?: boolean;
}

export interface ResearchScopeSelectorState {
  selectedRunIds: Set<string>;
  analysisFieldsByRun: Record<string, AnalysisField[]>;
  chunkIdsByRun: Record<string, number[]>;
  includeFullByRun: Record<string, boolean>;
  touched: boolean;
}

export function createResearchScopeState(runs: any[]): ResearchScopeSelectorState {
  return {
    selectedRunIds: new Set(
      runs
        .filter((run: any) => run.isActiveForPosts && run.status === 'completed' && run.analysis)
        .map((run: any) => run.id),
    ),
    analysisFieldsByRun: {},
    chunkIdsByRun: {},
    includeFullByRun: {},
    touched: false,
  };
}

export function buildResearchSelection(
  state: ResearchScopeSelectorState,
): ResearchRunSelection[] | undefined {
  if (!state.touched) return undefined;
  return Array.from(state.selectedRunIds).map((runId) => ({
    runId,
    includeAnalysisFields: state.analysisFieldsByRun[runId],
    includeChunkIds: state.chunkIdsByRun[runId],
    includeFullResearch: state.includeFullByRun[runId] || undefined,
  }));
}

export interface ResearchScopeSelectorProps {
  runs: any[];
  state: ResearchScopeSelectorState;
  onChange: (next: ResearchScopeSelectorState) => void;
  dense?: boolean;
}

export const ResearchScopeSelector: React.FC<ResearchScopeSelectorProps> = ({
  runs,
  state,
  onChange,
  dense,
}) => {
  if (runs.length === 0) return null;

  const setState = (patch: Partial<ResearchScopeSelectorState>) => {
    onChange({ ...state, ...patch, touched: true });
  };

  const toggleRun = (runId: string) => {
    const next = new Set(state.selectedRunIds);
    if (next.has(runId)) next.delete(runId);
    else next.add(runId);
    setState({ selectedRunIds: next });
  };

  const toggleAnalysisField = (runId: string, field: AnalysisField) => {
    const current = state.analysisFieldsByRun[runId] ?? [...ALL_ANALYSIS_FIELDS];
    const next = current.includes(field)
      ? current.filter((f) => f !== field)
      : [...current, field];
    setState({ analysisFieldsByRun: { ...state.analysisFieldsByRun, [runId]: next } });
  };

  const toggleChunk = (runId: string, chunkId: number) => {
    const current = state.chunkIdsByRun[runId] ?? [];
    const next = current.includes(chunkId)
      ? current.filter((id) => id !== chunkId)
      : [...current, chunkId].sort((a, b) => a - b);
    setState({ chunkIdsByRun: { ...state.chunkIdsByRun, [runId]: next } });
  };

  const toggleFull = (runId: string) => {
    setState({
      includeFullByRun: {
        ...state.includeFullByRun,
        [runId]: !state.includeFullByRun[runId],
      },
    });
  };

  return (
    <Box data-testid="research-scope-selector">
      <Typography variant="subtitle2" gutterBottom>
        Research scope ({state.selectedRunIds.size}/{runs.length} studies)
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Active completed studies are selected by default. You can include analysed fields,
        selected citation chunks, or the full Phase 2 report for experiments.
      </Typography>
      <Stack spacing={dense ? 0.75 : 1}>
        {runs.map((run: any) => {
          const selected = state.selectedRunIds.has(run.id);
          const fields = state.analysisFieldsByRun[run.id] ?? ALL_ANALYSIS_FIELDS;
          const chunkIds = state.chunkIdsByRun[run.id] ?? [];
          const chunks = (run.research?.chunks ?? []) as any[];
          const title = run.title ?? run.focus?.label ?? run.brief?.refinedDescription ?? run.research?.query ?? run.id;
          return (
            <Box
              key={run.id}
              sx={{
                p: dense ? 0.75 : 1,
                borderRadius: 1,
                border: '1px solid',
                borderColor: selected ? 'primary.main' : 'divider',
                opacity: selected ? 1 : 0.65,
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={selected}
                    onChange={() => toggleRun(run.id)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {String(title).slice(0, 120)}
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {run.focus?.kind && <Chip size="small" variant="outlined" label={run.focus.kind} />}
                      {run.isActiveForPosts && <Chip size="small" color="success" label="active" />}
                      <Chip size="small" variant="outlined" label={run.status ?? 'unknown'} />
                    </Stack>
                  </Box>
                }
              />
              {selected && (
                <Stack spacing={0.75} sx={{ ml: 4, mt: 0.5 }}>
                  {run.analysis && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Analysis fields</Typography>
                      <Box sx={{ mt: 0.5 }}>
                        {ALL_ANALYSIS_FIELDS.map((field) => {
                          const value = run.analysis?.[field];
                          if (!value || (Array.isArray(value) && value.length === 0)) return null;
                          const checked = fields.includes(field);
                          return (
                            <Chip
                              key={field}
                              size="small"
                              label={`${field}${Array.isArray(value) ? ` (${value.length})` : ''}`}
                              onClick={() => toggleAnalysisField(run.id, field)}
                              color={checked ? 'primary' : 'default'}
                              variant={checked ? 'filled' : 'outlined'}
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                  {chunks.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Phase 2 citation chunks</Typography>
                      <Box sx={{ mt: 0.5 }}>
                        {chunks.slice(0, 10).map((chunk: any, idx: number) => {
                          const checked = chunkIds.includes(idx);
                          return (
                            <Chip
                              key={idx}
                              size="small"
                              label={`${idx + 1}. ${String(chunk.title ?? chunk.url ?? 'source').slice(0, 42)}`}
                              onClick={() => toggleChunk(run.id, idx)}
                              color={checked ? 'primary' : 'default'}
                              variant={checked ? 'filled' : 'outlined'}
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                  {run.research?.aiSummary && (
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={Boolean(state.includeFullByRun[run.id])}
                          onChange={() => toggleFull(run.id)}
                        />
                      }
                      label="Include full Phase 2 report"
                    />
                  )}
                </Stack>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

export default ResearchScopeSelector;
